import { request, BASE_URL } from '@/api/client';
import { blobToBase64 } from '@/utils/file';
import { toAbsoluteMediaUrl } from '@/utils/url';
import type {
  SendMessageRequest,
  MessageResponse,
  ChatHistoryResponse,
  AudioChatResponse,
  ConversationWithUnread,
  MarkReadResponse,
  UnreadCountResponse,
  GenerationStatusResponse,
} from '@/types';

/**
 * Interface for Server-Sent Events emitted by the stream.
 */
export interface SSEEvent {
  event: string;
  data: unknown;
}

/**
 * Dispatches one parsed SSE frame to `onEvent`. JSON-decodes `data` when possible,
 * passes the raw string when not.
 */
export function flushSSEFrame(
  frame: { event?: string; data?: string },
  onEvent: (evt: SSEEvent) => void
): void {
  if (!frame.event) return;
  let payload: unknown = {};
  if (frame.data) {
    try {
      payload = JSON.parse(frame.data);
    } catch {
      payload = frame.data;
    }
  }
  onEvent({ event: frame.event, data: payload });
}

/**
 * Parses an SSE frame's worth of text (a `\n\n`-terminated chunk) into
 * `{ event, data }`. Multiple `data:` lines are joined with `\n`, per spec.
 */
export function parseSSELines(chunk: string): { event?: string; data?: string } {
  const lines = chunk.split(/\r?\n/);
  const frame: { event?: string; data?: string } = {};
  for (const line of lines) {
    if (line.startsWith('event:')) {
      frame.event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      const raw = line.slice(5).trim();
      frame.data = frame.data ? `${frame.data}\n${raw}` : raw;
    }
  }
  return frame;
}

/**
 * Drains an SSE `ReadableStream` reader, splitting it into frames and dispatching each.
 * Returns when the stream closes; aborting the underlying fetch makes `reader.read()`
 * reject with `AbortError` which propagates to the caller.
 */
export async function readSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (evt: SSEEvent) => void
): Promise<void> {
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      flushSSEFrame(parseSSELines(chunk), onEvent);
    }
  }
}

/** Re-throws an SSE-stream fetch failure as the same shape `request()` does (preserves AbortError). */
export function handleStreamFetchError(networkError: unknown): never {
  if ((networkError as Error).name === 'AbortError') throw networkError;
  throw Object.assign(new Error('Cannot connect to server. Please check your connection.'), {
    status: 0,
    isNetworkError: true,
    originalError: networkError,
  });
}

/** Throws a structured Error from a non-2xx SSE response, parsing the body as JSON when it is. */
export async function handleStreamResponseError(resp: Response): Promise<never> {
  let errBody: unknown = undefined;
  try {
    errBody = await resp.json();
  } catch {
    /* ignore */
  }
  const error = (errBody as Record<string, unknown>) ?? {
    message: resp.statusText,
    status: resp.status,
  };
  throw Object.assign(
    new Error(String((error as Record<string, unknown>)['message'] ?? resp.statusText)),
    { ...error, isNetworkError: false }
  );
}

/** Echo chat endpoints — text/audio send, paginated history, conversation management, plus the SSE stream entry. */
export const chatService = {
  sendMessage: (payload: SendMessageRequest): Promise<MessageResponse> =>
    request<MessageResponse>('/chat/send', {
      method: 'POST',
      body: JSON.stringify(payload),
      auth: true,
    }),

  /**
   * Sends an audio message with automatic transcription.
   * Handles MIME type sanitization to prevent 415 errors.
   */
  sendAudioMessage: async (
    echoId: string,
    audioBlob: Blob,
    ttsProvider: 'azure' | 'elevenlabs' = 'azure',
    generateAudio = true
  ): Promise<AudioChatResponse> => {
    const formData = new FormData();
    formData.append('echo_id', echoId);
    formData.append('tts_provider', ttsProvider);
    formData.append('generate_audio', String(generateAudio));

    // Sanitize MIME type (strip ';codecs=...') to pass strict backend validation
    const rawType =
      typeof audioBlob.type === 'string' && audioBlob.type.length > 0
        ? audioBlob.type
        : 'audio/webm';
    const cleanType = rawType.split(';')[0]; // e.g., "audio/webm" from "audio/webm;codecs=opus"

    // Map to correct file extension for the sanitized type
    let extension = 'webm';
    if (cleanType === 'audio/mp4') extension = 'm4a';
    else if (cleanType === 'audio/mpeg') extension = 'mp3';
    else if (cleanType === 'audio/wav') extension = 'wav';
    else if (cleanType === 'audio/ogg') extension = 'ogg';

    // Rewrap blob into a File with the sanitized type and matching extension
    // Ensure a definite string for FilePropertyBag to satisfy exactOptionalPropertyTypes
    const safeType: string = cleanType || 'audio/webm';
    const file = new File([audioBlob], `voice_message.${extension}`, { type: safeType });
    formData.append('audio', file);

    return request<AudioChatResponse>('/chat/audio', {
      method: 'POST',
      body: formData,
      auth: true,
    });
  },

  getHistory: (
    echoId: string,
    offset = 0,
    limit = 20,
    signal?: AbortSignal
  ): Promise<ChatHistoryResponse> =>
    request<ChatHistoryResponse>(
      `/chat/history/${encodeURIComponent(echoId)}?offset=${encodeURIComponent(offset)}&limit=${encodeURIComponent(limit)}`,
      {
        method: 'GET',
        auth: true,
        signal: signal ?? null,
      }
    ),

  /**
   * Streams chat responses using Server-Sent Events (SSE).
   * Handles the fetch connection, decoding, and event parsing internally.
   */
  streamChat: async (
    echoId: string,
    content: string,
    ttsProvider: 'azure' | 'elevenlabs',
    onEvent: (evt: SSEEvent) => void,
    signal?: AbortSignal,
    generateAudio = true,
    generateVideo = false
  ): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Not authenticated');

    const url = `${BASE_URL}/chat/stream/${encodeURIComponent(echoId)}?content=${encodeURIComponent(content)}&tts_provider=${encodeURIComponent(ttsProvider)}&generate_audio=${generateAudio}&generate_video=${generateVideo}`;

    let resp: Response;
    try {
      resp = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
        signal: signal ?? null,
      });
    } catch (networkError) {
      handleStreamFetchError(networkError);
    }

    if (!resp.ok || !resp.body) {
      return handleStreamResponseError(resp);
    }

    const reader = resp.body.getReader();
    try {
      await readSSEStream(reader, onEvent);
    } finally {
      reader.cancel().catch(() => {
        /* stream already closed */
      });
    }
  },

  clearConversation: (echoId: string): Promise<void> =>
    request<void>(`/chat/history/${encodeURIComponent(echoId)}`, {
      method: 'DELETE',
      auth: true,
    }),

  getMemoryStats: (
    echoId: string
  ): Promise<{ total_memories: number; recent_memories: number; [key: string]: unknown }> =>
    request<{ total_memories: number; recent_memories: number; [key: string]: unknown }>(
      `/chat/echoes/${encodeURIComponent(echoId)}/memory-stats`,
      { method: 'GET', auth: true }
    ),

  /**
   * Fetches the audio file from the server and converts it to a base64 string.
   * Used for playing back historical audio.
   */
  fetchMessageAudio: async (audioPath: string): Promise<string> => {
    const fullUrl = toAbsoluteMediaUrl(audioPath);
    const response = await fetch(fullUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch audio: ${response.statusText}`);
    }
    const blob = await response.blob();
    return blobToBase64(blob);
  },

  /**
   * Gets all conversations with unread message counts.
   * Returns conversations sorted by most recent message.
   */
  getConversations: (): Promise<ConversationWithUnread[]> =>
    request<ConversationWithUnread[]>('/chat/conversations', {
      method: 'GET',
      auth: true,
    }),

  /**
   * Marks all AI messages in a conversation as read.
   * Call this when the user enters/views a conversation.
   */
  markConversationAsRead: (echoId: string): Promise<MarkReadResponse> =>
    request<MarkReadResponse>(`/chat/${encodeURIComponent(echoId)}/mark-read`, {
      method: 'POST',
      auth: true,
    }),

  /**
   * Gets the unread message count for a specific echo conversation.
   * Returns 0 if no conversation exists yet.
   */
  getUnreadCount: (echoId: string): Promise<UnreadCountResponse> =>
    request<UnreadCountResponse>(`/chat/${encodeURIComponent(echoId)}/unread-count`, {
      method: 'GET',
      auth: true,
    }),

  /**
   * Checks if a background generation is in progress for this echo.
   * Used to show typing bubble when returning to a chat.
   */
  getGenerationStatus: (echoId: string): Promise<GenerationStatusResponse> =>
    request<GenerationStatusResponse>(`/chat/${encodeURIComponent(echoId)}/generation-status`, {
      method: 'GET',
      auth: true,
    }),

  generateMessageVideo: (
    messageId: string
  ): Promise<{ message_id: string; video_status: string }> =>
    request<{ message_id: string; video_status: string }>(
      `/chat/messages/${encodeURIComponent(messageId)}/video/generate`,
      { method: 'POST', auth: true }
    ),

  getMessageVideoStatus: (
    messageId: string
  ): Promise<{
    message_id: string;
    video_status: string;
    video_url?: string;
    video_error?: string;
  }> =>
    request<{ message_id: string; video_status: string; video_url?: string; video_error?: string }>(
      `/chat/messages/${encodeURIComponent(messageId)}/video/status`,
      { method: 'GET', auth: true }
    ),
};
